/** Guided setup onboarding helpers. */

/**
 * Guided setup is gated behind an env flag and is off unless explicitly enabled.
 * Controls both the auto-redirect into setup and the setup entry points in the UI.
 */
export function isGuidedSetupEnabled(): boolean {
  return import.meta.env.VITE_GUIDED_SETUP_ENABLED === 'true';
}

/**
 * Audit trail read surface in Settings. Off unless explicitly enabled, while the
 * write side is validated in prod.
 */
export function isAuditTrailEnabled(): boolean {
  return import.meta.env.VITE_AUDIT_TRAIL_ENABLED === 'true';
}

/** Quick Start hub dashboard. Defaults on when guided setup is enabled. */
export function isQuickStartHubEnabled(): boolean {
  const hub = import.meta.env.VITE_QUICK_START_HUB;
  if (hub === 'false') return false;
  if (hub === 'true') return true;
  return isGuidedSetupEnabled();
}

/** Explicit wizard entry via URL (header link or deep link). */
export function isSetupWizardMode(searchParams: URLSearchParams): boolean {
  return searchParams.get('mode') === 'wizard' || Boolean(searchParams.get('step'));
}

/**
 * Hub when the org has ≥1 store; guided wizard when store-less (or `?mode=wizard`).
 * Falls back to wizard when the hub feature flag is off.
 */
export function shouldShowQuickStartHub(
  hasStores: boolean,
  searchParams: URLSearchParams,
): boolean {
  if (!isQuickStartHubEnabled()) return false;
  if (!hasStores) return false;
  return !isSetupWizardMode(searchParams);
}

/** Wizard stays full-screen; hub uses the normal CRM shell with sidebar + nav. */
export function shouldUseSetupFullscreenLayout(
  hasStores: boolean,
  searchParams: URLSearchParams,
  isStoresLoading = false,
): boolean {
  if (!isQuickStartHubEnabled()) return true;
  if (isStoresLoading) return true;
  if (!hasStores) return true;
  return isSetupWizardMode(searchParams);
}

export function getSetupPath(orgSlug: string): string {
  return `/org/${orgSlug}/setup`;
}

export const SETUP_EXEMPT_PATH_SEGMENTS = [
  '/setup',
  '/activate-account',
  '/setup-payment',
  '/restricted',
] as const;

export function isSetupRoute(pathname: string): boolean {
  const normalized = pathname.replace(/\/$/, '') || pathname;
  return /\/setup$/.test(normalized);
}

export function isSetupExemptPath(pathname: string): boolean {
  return SETUP_EXEMPT_PATH_SEGMENTS.some((segment) => pathname.includes(segment));
}
